import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { MARKET_PROMOTION_PERMISSIONS } from '@/constants/permissions';
import {
  createActionsColumn,
  createSerialColumn,
  DrillDownCount,
  DrillDownCountButton,
  getAreaColumn,
  MemberNameCell,
  StackedCellText,
  WrappingCellText,
} from '@/components/common/DataTable';
import { ExpandableComment } from '@/components/common/ExpandableComment';
import { HighlightText } from '@/components/common/HighlightText';
import { RowActionList, RowViewLink, type RowAction } from '@/components/common/RowActions';
import { StatusBadge } from '@/components/common/StatusBadge';
import { EMPTY_VALUE } from '@/constants/app.constants';
import type { PromoDrillParams } from '@/features/promotion/types/promotion.types';
import { joinAreaNames } from '@/utils/areaNames';
import { formatDisplayDate, formatTime } from '@/utils/date';
import { formatNumber, pluralize } from '@/utils/number';
import {
  CALL_RESULT_LABEL,
  DONATION_DECISION_STEP,
  REGION_DONATIONS_PATH,
  TEACHER_ANSWER_LABEL,
} from '../constants/marketPromotion.constants';
import type {
  MpAdjustmentRow,
  MpApprovalRow,
  MpRegionDonationsSearch,
  MpRegionProjectRow,
  MpRegionRow,
  MpSummaryRow,
} from '../types/marketPromotion.types';
import { getDonationDecisionStep } from '../utils/donationDecision';
import { DecisionStatusBadge } from './DecisionStatusBadge';
import { DonationFileIcon } from './DonationFileIcon';
import { InstituteNumbers } from '@/components/common/InstituteNumbers';

const dash = (value: string | null | undefined): string =>
  value === null || value === undefined || value === '' ? EMPTY_VALUE : value;

const taka = (value: number) => <span className="tabular-nums">{formatNumber(value)}</span>;

export const APPROVAL_ZONAL_MANAGER_COLUMN_ID = 'zonalManager';

export function getMpAdjustmentColumns(
  zmSearch: PromoDrillParams,
  searchText: string | undefined,
): ColumnDef<MpAdjustmentRow, unknown>[] {
  return [
    createSerialColumn<MpAdjustmentRow>(),
    {
      accessorKey: 'name',
      header: 'Donation Project',
      cell: ({ row }) => (
        <WrappingCellText className="font-medium">
          <HighlightText text={row.original.name} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    {
      accessorKey: 'itemNo',
      header: 'Project No',
      cell: ({ row }) => <span className="tabular-nums">{row.original.itemNo}</span>,
    },
    {
      accessorKey: 'itemCode',
      header: 'Project Code',
      cell: ({ row }) => (
        <HighlightText text={dash(row.original.itemCode)} highlightedText={searchText} />
      ),
    },
    {
      accessorKey: 'distributedAmount',
      header: 'Distributed (Tk)',
      cell: ({ row }) => taka(row.original.distributedAmount),
    },
    createActionsColumn<MpAdjustmentRow>((item) => (
      <RowViewLink
        to="/market-promotion/adjustment/$promoItemId"
        params={{ promoItemId: item.promoItemId }}
        search={zmSearch}
        label="View ZMs"
        aria-label={`ZMs who handed out ${item.name}`}
      />
    )),
  ];
}

export function getMpSummaryColumns(
  searchText: string | undefined,
  onOpenProjects: (holder: MpSummaryRow) => void,
): ColumnDef<MpSummaryRow, unknown>[] {
  return [
    createSerialColumn<MpSummaryRow>(),
    {
      accessorKey: 'name',
      header: 'Budget Holder',
      cell: ({ row }) => (
        <MemberNameCell
          member={{
            name: row.original.name,
            hrEmpId: row.original.hrEmpId,
            mobile: row.original.userMobile,
          }}
          searchText={searchText}
        />
      ),
    },
    {
      accessorKey: 'departmentName',
      header: 'Department / Designation',
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.departmentName)}
          secondary={row.original.designationName}
        />
      ),
    },
    getAreaColumn<MpSummaryRow>(),
    {
      id: 'budgetProjects',
      header: 'Donation Projects',
      cell: ({ row }) => {
        const projectCount = row.original.budgetProjects.length;
        return (
          <DrillDownCountButton
            count={projectCount}
            label="Projects"
            shouldShowHint={false}
            hint={`View the budget of ${formatNumber(projectCount)} ${pluralize(projectCount, 'donation project')} of ${row.original.name}`}
            onOpen={() => onOpenProjects(row.original)}
          />
        );
      },
    },
    {
      accessorKey: 'budgetAmount',
      header: 'Budget (Tk)',
      cell: ({ row }) => taka(row.original.budgetAmount),
    },
    {
      accessorKey: 'disburseAmount',
      header: 'Disburse (Tk)',
      cell: ({ row }) => taka(row.original.disburseAmount),
    },
    {
      accessorKey: 'distributedAmount',
      header: 'Distributed (Tk)',
      cell: ({ row }) => taka(row.original.distributedAmount),
    },
    {
      accessorKey: 'approvedAmount',
      header: 'Approved (Tk)',
      cell: ({ row }) => taka(row.original.approvedAmount),
    },
    {
      accessorKey: 'duesAmount',
      header: 'Dues (Tk)',
      cell: ({ row }) => taka(row.original.duesAmount),
    },
    {
      accessorKey: 'inHandAmount',
      header: 'In Hand (Tk)',
      cell: ({ row }) => (
        <span className={row.original.inHandAmount < 0 ? 'text-destructive' : undefined}>
          {taka(row.original.inHandAmount)}
        </span>
      ),
    },
  ];
}

function getRegionDonationsLinkProps(drillSearch: MpRegionDonationsSearch) {
  return { to: REGION_DONATIONS_PATH, search: drillSearch } as const;
}

function getDistributedDrillColumn<TRow extends { distributedAmount: number }>(
  getDrillSearch: (row: TRow) => MpRegionDonationsSearch,
  getName: (row: TRow) => string,
): ColumnDef<TRow, unknown> {
  return {
    accessorKey: 'distributedAmount',
    header: 'Distributed (Tk)',
    cell: ({ row }) => (
      <DrillDownCount
        count={row.original.distributedAmount}
        hint={`View the donations of ${getName(row.original)}`}
        render={<Link {...getRegionDonationsLinkProps(getDrillSearch(row.original))} />}
      />
    ),
  };
}

function getDonationsViewColumn<TRow>(
  getDrillSearch: (row: TRow) => MpRegionDonationsSearch,
  getName: (row: TRow) => string,
): ColumnDef<TRow, unknown> {
  return createActionsColumn<TRow>((row) => (
    <RowViewLink
      {...getRegionDonationsLinkProps(getDrillSearch(row))}
      aria-label={`The donations of ${getName(row)}`}
    />
  ));
}

export function getMpRegionColumns(
  searchText: string | undefined,
  getDrillSearch: (region: MpRegionRow) => MpRegionDonationsSearch,
): ColumnDef<MpRegionRow, unknown>[] {
  const getName = (region: MpRegionRow) => region.regionName;
  return [
    createSerialColumn<MpRegionRow>(),
    {
      accessorKey: 'regionName',
      header: 'Region / Side',
      cell: ({ row }) => (
        <StackedCellText
          primary={<HighlightText text={row.original.regionName} highlightedText={searchText} />}
          primaryClassName="font-medium"
          secondary={
            <HighlightText text={dash(row.original.sideRegion)} highlightedText={searchText} />
          }
        />
      ),
    },
    {
      accessorKey: 'rsmName',
      header: 'Responsible Employee',
      cell: ({ row }) => <HighlightText text={row.original.rsmName} highlightedText={searchText} />,
    },
    {
      accessorKey: 'smName',
      header: 'Reporting To',
      cell: ({ row }) => (
        <HighlightText text={dash(row.original.smName)} highlightedText={searchText} />
      ),
    },
    getDistributedDrillColumn(getDrillSearch, getName),
    getDonationsViewColumn(getDrillSearch, getName),
  ];
}

export function getMpRegionProjectColumns(
  searchText: string | undefined,
  getDrillSearch: (project: MpRegionProjectRow) => MpRegionDonationsSearch,
): ColumnDef<MpRegionProjectRow, unknown>[] {
  const getName = (project: MpRegionProjectRow) => project.itemName;
  return [
    createSerialColumn<MpRegionProjectRow>(),
    {
      accessorKey: 'itemCode',
      header: 'Project Code',
      cell: ({ row }) => (
        <HighlightText text={dash(row.original.itemCode)} highlightedText={searchText} />
      ),
    },
    {
      accessorKey: 'itemName',
      header: 'Donation Project',
      cell: ({ row }) => (
        <WrappingCellText className="font-medium">
          <HighlightText text={row.original.itemName} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    getDistributedDrillColumn(getDrillSearch, getName),
    getDonationsViewColumn(getDrillSearch, getName),
  ];
}

interface ApprovalActions {
  onRecommend?: (row: MpApprovalRow) => void;
  onDecide?: (row: MpApprovalRow) => void;
  onView: (row: MpApprovalRow) => void;
  onVerify?: (row: MpApprovalRow) => void;
}

export function getMpApprovalColumns(
  actions: ApprovalActions,
  showCall: boolean,
  searchText: string | undefined,
): ColumnDef<MpApprovalRow, unknown>[] {
  const highlight = (text: string | null): ReactNode =>
    text === null || text === '' ? (
      EMPTY_VALUE
    ) : (
      <HighlightText text={text} highlightedText={searchText} />
    );

  const columns: ColumnDef<MpApprovalRow, unknown>[] = [
    createSerialColumn<MpApprovalRow>(),
    {
      id: APPROVAL_ZONAL_MANAGER_COLUMN_ID,
      header: 'ZM Name',
      cell: ({ row }) => {
        const zonalManagerNumber = showCall
          ? row.original.enteredByMobile
          : row.original.enteredByContact;
        return (
          <StackedCellText
            primary={highlight(row.original.enteredByName)}
            primaryClassName="font-medium"
            secondary={zonalManagerNumber ? highlight(zonalManagerNumber) : null}
          />
        );
      },
    },
    getAreaColumn<MpApprovalRow>((approval) => ({
      divisionName: approval.enteredByDivision,
      districtName: approval.enteredByDistrict,
      thanaName: approval.enteredByThana,
    })),
    {
      accessorKey: 'instituteName',
      header: 'Institute',
      cell: ({ row }) => (
        <StackedCellText
          primary={highlight(row.original.instituteName)}
          secondary={
            <InstituteNumbers
              code={row.original.instituteCode}
              eiin={row.original.instituteEiin}
              highlightedText={searchText}
            />
          }
          tertiary={joinAreaNames([
            row.original.instituteThana,
            row.original.instituteDistrict,
            row.original.instituteDivision,
          ])}
        />
      ),
    },
    {
      accessorKey: 'teacherMobile',
      header: 'Teacher',
      cell: ({ row }) => (
        <StackedCellText
          primary={highlight(row.original.teacherName)}
          secondary={row.original.teacherMobile ? highlight(row.original.teacherMobile) : null}
        />
      ),
    },
    { accessorKey: 'itemName', header: 'Donation Name', cell: ({ row }) => row.original.itemName },
    {
      accessorKey: 'enteredAt',
      header: 'Entry Date',
      cell: ({ row }) => (
        <StackedCellText
          primary={formatDisplayDate(row.original.enteredAt)}
          secondary={formatTime(row.original.enteredAt)}
        />
      ),
    },
    {
      accessorKey: 'claimedAmount',
      header: 'Claimed (Tk)',
      cell: ({ row }) => taka(row.original.claimedAmount),
    },
    {
      accessorKey: 'approvedAmount',
      header: 'Approved (Tk)',
      cell: ({ row }) => taka(row.original.approvedAmount),
    },
    {
      id: 'attachment',
      header: 'File',
      cell: ({ row }) => (row.original.hasAttachment ? <DonationFileIcon /> : EMPTY_VALUE),
    },
    {
      accessorKey: 'approveTypeNo',
      header: 'Status',
      cell: ({ row }) => <DecisionStatusBadge approveTypeNo={row.original.approveTypeNo} />,
    },
  ];

  if (showCall) {
    columns.push(
      {
        accessorKey: 'callTime',
        header: 'Call',
        cell: ({ row }) =>
          row.original.callTime ? (
            <StackedCellText
              primary={formatDisplayDate(row.original.callTime)}
              secondary={formatTime(row.original.callTime)}
              tertiary={row.original.calledBy}
            />
          ) : (
            EMPTY_VALUE
          ),
      },
      {
        accessorKey: 'teacherFeedback',
        header: 'Teacher Answer',
        cell: ({ row }) => dash(TEACHER_ANSWER_LABEL[row.original.teacherFeedback ?? 0]),
      },
      {
        accessorKey: 'callStatus',
        header: 'Call Result',
        cell: ({ row }) => dash(CALL_RESULT_LABEL[row.original.callStatus ?? 0]),
      },
      {
        accessorKey: 'misComment',
        header: 'MIS Comment',
        cell: ({ row }) =>
          row.original.misComment ? (
            <ExpandableComment comment={row.original.misComment} />
          ) : (
            EMPTY_VALUE
          ),
      },
      {
        accessorKey: 'isMisVerified',
        header: 'Verify Status',
        cell: ({ row }) => (
          <StatusBadge tone={row.original.isMisVerified ? 'success' : 'neutral'}>
            {row.original.isMisVerified ? 'Verified' : 'Not Verified'}
          </StatusBadge>
        ),
      },
    );
  }

  columns.push(
    createActionsColumn<MpApprovalRow>((approval) => (
      <RowActionList
        actions={getApprovalRowActions(approval, actions, showCall)}
        moreActionsAriaLabel={`More actions for ${approval.instituteName ?? 'this promotion'}`}
        shouldShowAllActions
      />
    )),
  );

  return columns;
}

function getApprovalRowActions(
  approval: MpApprovalRow,
  actions: ApprovalActions,
  showCall: boolean,
): RowAction[] {
  const { onRecommend, onDecide, onVerify } = actions;
  const instituteName = approval.instituteName ?? 'this promotion';
  const decisionStep = getDonationDecisionStep(approval);
  const rowActions: RowAction[] = [];

  if (decisionStep === DONATION_DECISION_STEP.recommend && onRecommend) {
    rowActions.push({
      key: 'recommend',
      label: 'Recommend',
      ariaLabel: `Recommend the promotion for ${instituteName}`,
      onSelect: () => onRecommend(approval),
      requires: MARKET_PROMOTION_PERMISSIONS.APPROVAL_APPROVE,
    });
  }
  if (decisionStep === DONATION_DECISION_STEP.approve && onDecide) {
    rowActions.push({
      key: 'approve',
      label: 'Approve',
      ariaLabel: `Approve the promotion for ${instituteName}`,
      onSelect: () => onDecide(approval),
      requires: MARKET_PROMOTION_PERMISSIONS.APPROVAL_APPROVE,
    });
  }
  if (!showCall) {
    rowActions.push({
      key: 'view',
      label: 'View',
      ariaLabel: `View the promotion for ${instituteName}`,
      onSelect: () => actions.onView(approval),
    });
  }
  if (showCall && onVerify) {
    rowActions.push({
      key: 'verify',
      label: approval.isMisVerified ? 'Verified' : 'Verify',
      ariaLabel: `Verify the promotion for ${instituteName}`,
      onSelect: () => onVerify(approval),
      requires: MARKET_PROMOTION_PERMISSIONS.MIS_APPROVAL_VERIFY,
    });
  }
  return rowActions;
}
